"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDynamicScript = void 0;
const react_1 = __importDefault(require("react"));
const debug_1 = __importDefault(require("debug"));
const log = (0, debug_1.default)('exchange:scripts');
/**
 * Will add a script to the browser DOM
 *
 * @example
 *
 * ```ts
 * import { useDynamicScript } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *   const { ready, failed } = useDynamicScript('http://mydomain/myscript.js');
 *
 *   if (ready) {
 *     return 'Script loaded';
 *   }
 *
 *   if (failed) {
 *     return 'Script failed';
 *   }
 *
 *   return 'Loading';
 * };
 * ```
 *
 * @param url The script URL to be loaded dynamically
 * @returns An object that represents the status of the script.
 */
const useDynamicScript = (url) => {
    const [ready, setReady] = react_1.default.useState(false);
    const [failed, setFailed] = react_1.default.useState(false);
    react_1.default.useEffect(() => {
        if (!url) {
            return null;
        }
        const element = document.createElement('script');
        element.src = url;
        element.type = 'text/javascript';
        element.async = true;
        setReady(false);
        setFailed(false);
        element.onload = () => {
            log(`Dynamic Script Loaded: ${url}`);
            setReady(true);
        };
        element.onerror = () => {
            log(`Dynamic Script Error: ${url}`);
            setReady(false);
            setFailed(true);
        };
        document.body.appendChild(element);
        return () => {
            document.body.removeChild(element);
        };
    }, [url]);
    return {
        ready,
        failed
    };
};
exports.useDynamicScript = useDynamicScript;
